import React, { useState } from 'react';
import { SavingTip } from '@/lib/data';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DropletIcon, FlameIcon, Trash2Icon, WrenchIcon, LightbulbIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface SavingTipsCardProps {
  tips: SavingTip[];
  className?: string;
}

const SavingTipsCard: React.FC<SavingTipsCardProps> = ({ tips, className }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('alle');

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'vann':
        return <DropletIcon className="h-4 w-4 text-blue-500" />;
      case 'renovasjon':
        return <Trash2Icon className="h-4 w-4 text-slate-600" />;
      case 'septik':
        return <FlameIcon className="h-4 w-4 text-amber-500" />;
      default:
        return <LightbulbIcon className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'enkel':
        return 'bg-slate-100 text-slate-700';
      case 'moderat':
        return 'bg-yellow-100 text-yellow-800';
      case 'avansert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTips = selectedCategory === 'alle' 
    ? tips 
    : tips.filter(tip => tip.category === selectedCategory);

  return (
    <Card className={`overflow-hidden ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-xl flex items-center gap-2">
          <LightbulbIcon className="h-5 w-5 text-yellow-500" />
          Spar penger på kommunale avgifter
        </CardTitle>
        <CardDescription>
          Smarte tips for å redusere dine årlige kommunale avgifter
        </CardDescription>
      </CardHeader>

      <Tabs defaultValue="alle" className="px-4 pb-2">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="alle" onClick={() => setSelectedCategory('alle')}>
            Alle
          </TabsTrigger>
          <TabsTrigger value="vann" onClick={() => setSelectedCategory('vann')}>
            <DropletIcon className="h-4 w-4 mr-1" /> Vann
          </TabsTrigger>
          <TabsTrigger value="renovasjon" onClick={() => setSelectedCategory('renovasjon')}>
            <Trash2Icon className="h-4 w-4 mr-1" /> Renovasjon
          </TabsTrigger>
          <TabsTrigger value="septik" onClick={() => setSelectedCategory('septik')}>
            <FlameIcon className="h-4 w-4 mr-1" /> Septik
          </TabsTrigger>
          <TabsTrigger value="generelt" onClick={() => setSelectedCategory('generelt')}>
            Generelt
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <CardContent className="p-0">
        <ScrollArea className="h-[450px] px-4">
          <div className="grid gap-4 py-2">
            {filteredTips.map(tip => (
              <div key={tip.id} className="border rounded-lg p-4 shadow-sm bg-card hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-base flex items-center gap-2">
                    {getCategoryIcon(tip.category)}
                    {tip.title}
                  </h3>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge className={getDifficultyColor(tip.difficulty)}>
                        {tip.difficulty}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Vanskelighetsgrad: {tip.difficulty}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{tip.description}</p>
                <div className="flex justify-between items-center mt-2">
                  <Badge variant="outline" className="text-sm font-semibold">
                    Estimert besparelse: {tip.estimatedSaving}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
      
      <CardFooter className="pt-3 border-t flex justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-100 text-slate-700">enkel</Badge>
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800">moderat</Badge>
          <Badge variant="outline" className="bg-red-100 text-red-800">avansert</Badge>
        </div>
        <span>Besparelser er estimater basert på gjennomsnittsberegninger</span>
      </CardFooter>
    </Card>
  );
};

export default SavingTipsCard; 